// Escape belongs to the TOPMOST layer, and to that layer only.
//
// The app has several surfaces that dismiss themselves on Escape by listening on `window` in the bubble
// phase (`ChatFilesPanel`, `TaskSidePanel`, `useFileSidePanel`). Every one of them fires on the same
// keystroke, so opening a modal ON TOP of one and pressing Escape unwound the whole pile at once: from
// the chat Files panel a single Escape closed the doc viewer AND the panel, and from the file side panel
// it additionally stripped `?file=` out of the URL.
//
// So the modal shell claims the key instead of sharing it:
//   - it listens in the CAPTURE phase on `window`, which runs before any bubble-phase listener regardless
//     of who registered first (the panels register on mount, long before the modal opens, so a
//     bubble-phase listener in the modal could never preempt them);
//   - it calls `stopImmediatePropagation()`, which ends the dispatch there — no other listener runs;
//   - and only the LAST-OPENED modal acts, so nested modals close one at a time, innermost first.
// Registration order can't express "innermost", hence the explicit stack: `handleModalEscape` always
// delegates to the top of it, whichever instance's listener happened to run.

type CloseHandler = () => void;

const stack: CloseHandler[] = [];

/** Registers `handler` as the innermost open layer. Returns the deregister function. */
export function pushModalEscape(handler: CloseHandler): () => void {
  stack.push(handler);
  return () => {
    const at = stack.lastIndexOf(handler);
    if (at >= 0) stack.splice(at, 1);
  };
}

/** The bits of `KeyboardEvent` this needs — jest runs on `testEnvironment: 'node'`, so the behaviour is
 *  pinned against a plain object rather than a real event. */
export interface EscapeKeyEvent {
  key: string;
  preventDefault(): void;
  stopImmediatePropagation(): void;
}

/** Handles an Escape keydown for the innermost open layer, swallowing the key so nothing underneath also
 *  reacts to it. Returns whether the key was consumed. */
export function handleModalEscape(event: EscapeKeyEvent): boolean {
  if (event.key !== 'Escape') return false;
  const innermost = stack[stack.length - 1];
  if (!innermost) return false;
  event.preventDefault();
  event.stopImmediatePropagation();
  innermost();
  return true;
}

/** Test seam: how many layers currently claim Escape. */
export const openModalEscapeCount = (): number => stack.length;
